import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * M8 — the queue as it stands at `observedAt`.
 *
 * Read from `ArenaEntry` directly. `GET v1/cede-arena/queue-status` is NOT
 * usable here: it adds CMS-configured display padding, so it reports a depth
 * the database does not have.
 *
 * `observedAt` is the caller-supplied instant, never an inline `now()`, and it
 * is the same clock the depth and the oldest wait are measured against.
 */
export const ArenaOpsMatchmakingQueueDepthResponseSchema = z.object({
  observedAt: z.iso.datetime(),
  rows: z.array(
    z.object({
      /**
       * The bucket's identity, and the key `oracle.sql` groups on — see the
       * note on route 27. Key on this, not on `tier` and `buyInUsdt` separately.
       */
      buyInLabel: z.string(),
      /** The label's tier half, split out. `SPIN_AND_GO`, `PAID` or `FREE`. */
      tier: z.string(),
      /** The label's amount half, split out. Decimal-USDT string. */
      buyInUsdt: ArenaOpsUsdtSchema,
      /** Every entry in a queue status. `proposedPending` is a SUBSET of this, not a sibling. */
      queuedEntries: z.number().int(),
      humansQueued: z.number().int(),
      botsQueued: z.number().int(),
      /** `PENDING` / `PENDING_NOTIFIED` — queued but the buy-in has not landed. */
      unpaid: z.number().int(),
      /** `PAID` / `PAID_NOTIFIED` — paid and waiting for an opponent. */
      paidWaiting: z.number().int(),
      /** `MATCH_PROPOSED` — matched, inside the 30s ready check. */
      proposedPending: z.number().int(),
      /** Age of the oldest wait in the bucket. Null only when the bucket is empty. */
      oldestWaitSeconds: z.number().nullable(),
    }),
  ),
});
export type ArenaOpsMatchmakingQueueDepthResponse = z.infer<typeof ArenaOpsMatchmakingQueueDepthResponseSchema>;
